import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';

/**
 * Big primary button: "Regenerate {Month} targets".
 *
 * Calls supabase.rpc('apply_tenant_objective_rules', { p_tenant_id, p_year, p_month }).
 * Shows progress + success toast/inline message with the count of agent_targets rows generated.
 *
 * Includes a small dropdown to pick a different month (defaults to current).
 */
const MONTHS = [
  { value: 1, key: 'jan' },
  { value: 2, key: 'feb' },
  { value: 3, key: 'mar' },
  { value: 4, key: 'apr' },
  { value: 5, key: 'may' },
  { value: 6, key: 'jun' },
  { value: 7, key: 'jul' },
  { value: 8, key: 'aug' },
  { value: 9, key: 'sep' },
  { value: 10, key: 'oct' },
  { value: 11, key: 'nov' },
  { value: 12, key: 'dec' },
];

const RegenerateTargetsButton = ({ tenantId, disabled }) => {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', text }

  const handleClick = async () => {
    if (!tenantId) return;
    setRunning(true);
    setResult(null);
    try {
      const { data, error } = await supabase.rpc('apply_tenant_objective_rules', {
        p_tenant_id: tenantId,
        p_year: year,
        p_month: month,
      });
      if (error) throw error;
      setResult({
        type: 'success',
        text: t(
          'admin.objectiveRules.regenerate.success',
          '{{count}} target rows generated.',
          { count: data ?? 0 }
        ),
      });
      // Auto-dismiss after a few seconds
      setTimeout(() => setResult(null), 5000);
    } catch (e) {
      setResult({
        type: 'error',
        text: e?.message || 'Regeneration failed.',
      });
    } finally {
      setRunning(false);
    }
  };

  const monthLabel = t(
    `admin.objectiveRules.month.${MONTHS.find((m) => m.value === month)?.key}`,
    new Date(year, month - 1, 1).toLocaleString(undefined, { month: 'long' })
  );

  // Generate year options (current ± 2)
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="flex flex-col items-stretch md:items-end gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          disabled={running || disabled}
          className="px-2 py-2 rounded-lg bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60"
        >
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {t(
                `admin.objectiveRules.month.${m.key}`,
                new Date(2000, m.value - 1, 1).toLocaleString(undefined, {
                  month: 'long',
                })
              )}
            </option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          disabled={running || disabled}
          className="px-2 py-2 rounded-lg bg-card border border-border text-foreground text-base tabular-nums outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleClick}
          disabled={running || disabled || !tenantId}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {running ? (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : (
            <span>↻</span>
          )}
          {running
            ? t('admin.objectiveRules.regenerate.running', 'Regenerating…')
            : t(
                'admin.objectiveRules.regenerate.cta',
                'Regenerate {{month}} {{year}} targets',
                { month: monthLabel, year }
              )}
        </button>
      </div>

      {result && (
        <div
          className={`text-xs px-3 py-2 rounded-lg ${
            result.type === 'success'
              ? 'bg-success/10 border border-success/30 text-success'
              : 'bg-error/10 border border-error/30 text-error'
          }`}
        >
          {result.text}
        </div>
      )}
    </div>
  );
};

export default RegenerateTargetsButton;
